import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { getUnauthorizedRedirect } from '../../guards/page-access.util';
import { ApiService } from '../../services/index';
import { selectedCompanyStorage } from '../../storage/company/index';
import { Valuation } from '../../types/events.types';
import { PageRoute } from '../../types/page.types';
import { useAppQuery } from '../use-app-query.hook';

export const useCurrent = ({
  companyId: companyIdOverride,
  defaultErrorHandling = true,
  enabled = true,
}: {
  companyId?: string;
  defaultErrorHandling?: boolean;
  enabled?: boolean;
} = {}) => {
  const companyIdFromStorage = selectedCompanyStorage(({ companyId }) => companyId);
  const company = selectedCompanyStorage(({ company }) => company);
  const companyId = companyIdOverride ?? companyIdFromStorage;
  const isCompanyContextReady = company?.id === companyId;
  const canReadCurrentValuation =
    company != null && getUnauthorizedRedirect(PageRoute.EVENTS, company) === null;

  const queryClient = useQueryClient();

  // isLoading, not isPending: react-query holds a disabled query at status 'pending' indefinitely,
  // so isPending would never clear while the query is gated on isCompanyContextReady.
  const { data: valuation, isLoading } = useAppQuery({
    queryKey: [QueryKey.GET_CURRENT_VALUATION, companyId],
    queryFn: () =>
      ApiService.get<Valuation>({
        endpoint: BackendRoute.COMPANIES,
        routePath: [companyId, 'events', 'valuations', 'current'],
      }),
    enabled: !!companyId && enabled && isCompanyContextReady && canReadCurrentValuation,
    defaultErrorHandling,
  });

  const invalidateQuery = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_CURRENT_VALUATION] });
  }, [queryClient]);

  return {
    invalidateQuery,
    valuation,
    isLoading,
  };
};
